import React, { useState } from 'react';
import axios from 'axios';
import { Button, TextInput } from '@opendate-app/setlist';

const slugify = (s) => s.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

const KINDS = {
  fund: {
    title: 'New fund',
    submitLabel: 'Create fund',
    namePlaceholder: 'e.g. General Fund',
    showCode: true,
    url: (teamId) => `/teams/${teamId}/funds.json`,
    payload: (name, code) => ({ fund: { name, fund_code: code } }),
  },
  campaign: {
    title: 'New campaign',
    submitLabel: 'Create campaign',
    namePlaceholder: 'e.g. Grand Opening',
    hint: 'Add a goal and dates later from Giving.',
    showCode: false,
    url: (teamId) => `/teams/${teamId}/giving.json`,
    payload: (name, code) => ({ giving_campaign: { name, campaign_code: code, format: 'form' } }),
  },
};

function errorMessageFrom(error, fallback) {
  const errors = error?.response?.data?.errors;
  return Array.isArray(errors) && errors.length > 0 ? errors.join(', ') : fallback;
}

export default function QuickCreateGivingModal({ kind, teamId, onCreated, onClose }) {
  const config = KINDS[kind];
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [codeTouched, setCodeTouched] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const effectiveCode = codeTouched ? code.trim() : slugify(name);

  const submit = async () => {
    if (submitting) return;
    const trimmedName = name.trim();
    if (!trimmedName || !effectiveCode) {
      setError(trimmedName ? 'Code is required' : 'Name is required');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const { data } = await axios.post(config.url(teamId), config.payload(trimmedName, effectiveCode));
      onCreated({ id: data.id, name: trimmedName });
    } catch (err) {
      setError(errorMessageFrom(err, `Could not create ${kind}. Try again.`));
      setSubmitting(false);
    }
  };

  return (
    <div className="pfm-mini-overlay" onClick={onClose}>
      <div
        className="pfm-mini-modal"
        role="dialog"
        aria-label={config.title}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key !== 'Enter') return;
          e.preventDefault();
          submit();
        }}
      >
        <div className="pfm-mini-modal__header">
          <div className="pfm-mini-modal__title">{config.title}</div>
          <button type="button" className="cfm-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        <div className="pfm-mini-modal__body">
          <div className="cfm-field">
            <TextInput
              label="Name"
              required
              autoFocus
              value={name}
              placeholder={config.namePlaceholder}
              hint={config.hint}
              error={error || undefined}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          {config.showCode && (
            <div className="cfm-field">
              <TextInput
                label="Code"
                required
                value={codeTouched ? code : slugify(name)}
                placeholder="short unique identifier"
                onChange={(e) => { setCode(e.target.value); setCodeTouched(true); }}
              />
            </div>
          )}
        </div>

        <div className="pfm-mini-modal__footer">
          <Button type="button" disabled={submitting} onClick={onClose}>Cancel</Button>
          <Button type="button" variant="primary" disabled={submitting || !name.trim()} onClick={submit}>
            {submitting ? 'Creating…' : config.submitLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
